/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import App from 'apmui/App';
import EventTags from 'apmui/EventTags';
import { IFormConfiguration, IFormField } from './Model';
import { findField } from 'apmui/common/modelAPI';
import IPageModel from 'apmui/page';

const app = App.get();

interface IPageWithForms {
    form?: IFormConfiguration;
    forms?: IFormConfiguration[];
}

/**
 * Pre-process form configuration received from the server.
 */
function initializeDefaultValues(form: IFormConfiguration) {
    if (!Array.isArray(form.fields)) {
        return;
    }
    for (const field of form.fields) {
        if ((field.disabled as any) === '0') {
            field.disabled = false;
        }
        /* 'select' and 'radio' are always initialized with the first option */
        if (!field.value && (field.type === 'select' || field.type === 'radio')) {
            field.value = field.options && field.options[0] && field.options[0].value;
        }
        /* Save default value received from the server */
        if (field.value && !field.default) {
            field.default = field.value as string;
        }
        /*
         * Checkbox submits .default and uses .value for tracking state.
         * Default value logic is based on a standard customization code of logon.inc.
         */
        if (field.type === 'checkbox') {
            field.value = Boolean(field.disabled && field.default);
            if (!field.default) {
                field.default = '1';
            }
        }
    }
}

app.subscribeDefault(EventTags.CONFIGURE, (_, store) => {
    const page = store[store.pageType] as IPageWithForms;

    /* iterate over all the forms in the page */
    if (page.forms) {
        page.forms.forEach(initializeDefaultValues);
    }
    if (page.form) {
        initializeDefaultValues(page.form);
    }
});

function getForm(store: IPageModel, formId: string) {
    const page = store[store.pageType] as IPageWithForms;
    if (page) {
        if (page.form) {
            return page.form;
        } else if (Array.isArray(page.forms)) {
            return page.forms.filter((x: IFormConfiguration) => x.id === formId)[0] || null;
        }
    }
    return null;
}

app.subscribeDefault(EventTags.FORM_RESET, ({ detail: formId }, store) => {
    const formState = getForm(store, formId);
    if (!formState || !formState.fields) {
        return;
    }

    for (const field of formState.fields) {
        field.value = typeof field.default !== 'undefined' ? field.default : '';
        field.error = undefined;
    }
});

app.subscribeDefault(EventTags.FORM_FIELD_CHANGED, ({ detail: change }, store) => {
    const formState = getForm(store, change.form);
    if (!formState || !formState.fields) {
        return;
    }

    const field = findField(formState, change.name);
    if (field) {
        field.value = change.value;
        /* reset error state after any change */
        field.error = undefined;
    }
});

app.subscribeDefault(EventTags.FORM_VALIDATE, ({ detail }) => {
    /*
     * Do not validate by default; all validations will be page-specific.
     * Check if any of previous controllers indicated validation error and act accordingly.
     */
    if (detail.errorMessage || Object.keys(detail.errors).length > 0) {
        app.dispatch(EventTags.FORM_VALIDATION_ERROR, detail);
    } else {
        app.dispatch(EventTags.FORM_SUBMIT, detail.form);
    }
});

app.subscribeDefault(EventTags.FORM_VALIDATION_ERROR, ({ detail }, store) => {
    const formState = getForm(store, detail.form);
    if (!formState || !formState.fields) {
        return;
    }

    /* Display validation errors */
    for (const field of formState.fields) {
        if (detail.errors[field.name]) {
            field.error = detail.errors[field.name];
        }
    }
});

import h from 'apmui/util/createElement';

function submitFormConfiguration(form: IFormConfiguration) {
    function shouldSubmitField(field: IFormField) {
        /* disabled fields are not submitted */
        if (field.disabled) {
            return false;
        }
        switch (field.type) {
            case 'none':
                return false;
            /* checkbox and radio are not submitted if unset */
            case 'checkbox':
                return Boolean(field.value);
            case 'radio':
                return typeof field.value !== 'undefined';
            default:
                return true;
        }
    }

    function prepareField(field: IFormField) {
        const props: any = {
            autocomplete: 'off',
            name: field.name,
            type: field.type,
            value: field.value,
        };

        switch (field.type) {
            case 'checkbox':
                props.value = field.default;
                props.checked = field.value;
                break;
            case 'radio':
            case 'select':
                /* Change type to 'text' instead of creating all required elements */
                props.type = 'text';
                break;
        }
        return h('input', props);
    }

    const fields: HTMLElement[] = form.fields.filter(shouldSubmitField).map(prepareField);

    const formElement: HTMLFormElement = h(
        'form',
        {
            action: form.action || '',
            autocomplete: 'off',
            hidden: true,
            method: form.method || 'POST',
        },
        ...fields
    );

    document.body.appendChild(formElement);
    formElement.submit();
}

app.subscribeDefault(EventTags.FORM_SUBMIT, ({ detail: formId }, store) => {
    const formState = getForm(store, formId);
    if (!formState) {
        return;
    }
    submitFormConfiguration(formState);
});
